<script setup lang="ts">
import type { TranslationKey } from '~/composables/translate/translations'
import { CtIcon } from '@camelot/components'
import { initTooltips, Tooltip, type TooltipOptions } from 'flowbite'
import { defineEmits, defineProps, onBeforeMount, onMounted, ref } from 'vue'
import { useBuildSeries } from '~/composables/build-series'
import { translate } from '~/composables/translate/translations'
import { useVehiclesStore } from '~/stores/vehicles'

const props = withDefaults(defineProps<{
  buildSeries?: string
  isMeta?: boolean
  opacity?: string
}>(), { isMeta: false, opacity: 'opacity-100' })

const emits = defineEmits(['tooltipClicked'])

const categoryBoxRef = ref<HTMLElement>()
const tooltipDiv = ref<HTMLDivElement>()

const { getLinkedBuildSeries, getCategory } = useVehiclesStore()
const { buildSeriesTranslation } = useBuildSeries(props.buildSeries)

onBeforeMount(() => initTooltips())

onMounted(() => {
  const options: TooltipOptions = { placement: 'bottom-start', triggerType: 'hover', onShow: emitShowTooltip, onHide: emitHideTooltip }
  // eslint-disable-next-line no-new
  new Tooltip(tooltipDiv.value, categoryBoxRef.value, options)
})

const buildSeriesTranslated = computed(() => buildSeriesTranslation() ?? `${translate('prebook_category')} ${getCategory()}`)

function formatLinkedBuildSeries() {
  const linkedBuildSeries = getLinkedBuildSeries(props.buildSeries)
  return linkedBuildSeries
    .map(series => translate(`pre_${series.toUpperCase()}` as TranslationKey))
    .join(' | ')
}

function emitShowTooltip() { emits('tooltipClicked', true) }
function emitHideTooltip() { emits('tooltipClicked', false) }
</script>

<template>
  <ul ref="categoryBoxRef" data-testid="vehicle-category-info">
    <li :class="[isMeta ? 'ct-title-3' : 'ct-title-4', opacity]" data-testid="vehicle-bs">
      {{ buildSeriesTranslated }}
    </li>
    <li>
      <p class="text-black-80 text-sm" :class="opacity">
        {{ translate('car_or_similar_prebook') }}
        <CtIcon size="28" name="info" color="--ct-color-black-60" data-testid="icon" />
      </p>

      <div
        ref="tooltipDiv"
        role="tooltip"
        class="invisible booking-tooltip absolute max-w-[340px]"
        data-testid="similar-build-series-tooltip"
      >
        <p class="mb-8">
          {{ translate('prebook_category') }} {{ getCategory(buildSeries) }}
        </p>
        <p>{{ formatLinkedBuildSeries() }}</p>
      </div>
    </li>
  </ul>
</template>
